/**
 * Types partagés entre le serveur et le client.
 * Ce paquet ne contient que du code pur : aucune entrée/sortie, aucune dépendance
 * à Node ou au navigateur.
 */

export type UserId = string;
export type MatchId = string;
export type GameId = string;

export type Role = 'player' | 'admin';
export type UserStatus = 'active' | 'suspended';

/** Représentation d'un compte telle qu'elle peut être envoyée au navigateur. */
export interface PublicUser {
  id: UserId;
  username: string;
  displayName: string;
  role: Role;
  status: UserStatus;
  createdAt: string;
  lastSeenAt: string | null;
}

export type MatchStatus = 'active' | 'finished' | 'aborted';

/** Cause de fin de partie. */
export type EndReason =
  | 'victory' // objectif atteint (flotte coulée, échec et mat…)
  | 'draw' // partie nulle
  | 'resign' // abandon volontaire
  | 'timeout' // chronomètre dépassé
  | 'admin'; // partie interrompue par un administrateur

export const END_REASON_LABEL: Record<EndReason, string> = {
  victory: 'Victoire',
  draw: 'Partie nulle',
  resign: 'Abandon',
  timeout: 'Temps écoulé',
  admin: 'Interrompue par un administrateur',
};

/** Option proposée à la création d'une partie solo (niveau, camp…). */
export interface GameOption {
  id: string;
  label: string;
}

/** Descripteur d'un jeu, exposé au lobby. */
export interface GameInfo {
  id: GameId;
  name: string;
  description: string;
  minPlayers: number;
  maxPlayers: number;
  /** Niveaux de l'adversaire artificiel ; liste vide = pas de mode solo. */
  soloLevels: GameOption[];
  /** Camps proposés en solo (ex. blancs / noirs). */
  soloSides: GameOption[];
}

/** Évènement de jeu diffusé aux joueurs (sert au journal de partie). */
export interface GameEvent {
  type: string;
  actorId?: UserId;
  at?: string;
  [key: string]: unknown;
}

export interface ChatMessage {
  id: string;
  matchId: MatchId;
  userId: UserId | null;
  username: string;
  body: string;
  createdAt: string;
}

export interface RatingRow {
  userId: UserId;
  username: string;
  displayName: string;
  gameId: GameId;
  rating: number;
  played: number;
  wins: number;
  losses: number;
}

export interface MatchSummary {
  id: MatchId;
  gameId: GameId;
  status: MatchStatus;
  createdAt: string;
  endedAt: string | null;
  endReason: EndReason | null;
  winnerId: UserId | null;
  players: Array<{
    userId: UserId;
    username: string;
    displayName: string;
    seat: number;
    ratingBefore: number | null;
    ratingAfter: number | null;
  }>;
}

/** Réglages globaux modifiables par l'administrateur. */
export interface PlatformSettings {
  /** Chronomètre activé pour les parties. */
  timerEnabled: boolean;
  /** Bataille navale : secondes accordées pour jouer un coup. */
  turnSeconds: number;
  /** Bataille navale : secondes accordées pour placer sa flotte. */
  placementSeconds: number;
  /** Bataille navale : un coup au but redonne la main au tireur. */
  extraTurnOnHit: boolean;
  /** Échecs : secondes accordées pour jouer un coup. */
  chessTurnSeconds: number;
}

export const DEFAULT_SETTINGS: PlatformSettings = {
  timerEnabled: true,
  turnSeconds: 60,
  placementSeconds: 180,
  extraTurnOnHit: false,
  chessTurnSeconds: 120,
};
